import {
  VisitContext,
  type VisitContextType,
  type Report,
  type ReportView,
  type Company,
  type Site,
  type DataContext,
  type ReportTemplate,
} from '../../../data/schemas';
import type { AppSettings } from '@/data/settings/defaultSettings';
import { DEFAULT_SETTINGS } from '@/data/settings/defaultSettings';
import type { ReportRenderDescriptor } from '../types/UnifiedReportTypes';
import { createReportView } from './createReportView';
import { TemplatesRepo } from '../../../data/repos/templatesRepo';
import { formatVisitControlPeriod } from '@/utils/periodicity';
import { parseAttendeeLine, splitAttendeeLines } from './attendees';
import { parseKeypath } from '@/data/idGenerator';
import { getCachedReportAttachment } from '@/data/sync';
import {
  SCORECARD_SECTION_ID,
  SCORECARD_DATE_FIELD_ID,
  SCORECARD_FIELD_TO_CONTEXT_KEY,
} from '@/data/scorecardConfig';

const VISIT_ATTENDEES_KEYPATH = 'sec_persone-presenti-al-sopralluogo.fld_partecipanti-sopralluogo';
const SCORECARD_FIELD_MAP: Record<string, string> = {
  ...SCORECARD_FIELD_TO_CONTEXT_KEY,
};

interface ReportAugmentationResult {
  report: Report;
  descriptor: ReportRenderDescriptor;
  view: ReportView;
}

interface AugmentReportOptions {
  includeHighlighting?: boolean;
  includeEditUrls?: boolean;
  baseEditUrl?: string;
  template?: ReportTemplate | null;
}

/**
 * Augment report for rendering
 *
 * Generates a ReportView from template + data for presentation.
 * Loads template, resolves values, and creates normalized view blocks.
 */
export async function augmentReportForRendering(
  report: Report,
  company: Company | null,
  site?: Site | null,
  _settings?: AppSettings,
  _options: AugmentReportOptions = {}
): Promise<ReportAugmentationResult> {
  // Load template
  const template =
    _options.template ??
    (await TemplatesRepo.getVersion(report.templateId, report.templateVersion));

  if (!template) {
    throw new Error(
      `Template ${report.templateId}@${report.templateVersion} not found. ` +
        `Cannot render report ${report.id}.`
    );
  }

  // Generate view with live context for drafts
  const visitDate = report.meta.visitDate;
  const visitPeriodKey = 'sec_quadro-riepilogativo-dellispezione.fld_periodo-di-controllo';
  const visitPeriodValue = report.data.values[visitPeriodKey];
  const frequencyMonths = site?.periodicity ? parseInt(site.periodicity, 10) : undefined;
  const computedVisitPeriod =
    typeof visitPeriodValue === 'string' && visitPeriodValue.trim().length > 0
      ? visitPeriodValue
      : formatVisitControlPeriod(visitDate, frequencyMonths);

  const nostroRecapito = _settings?.nostroRecapito ?? DEFAULT_SETTINGS.nostroRecapito;

  const context = buildDraftContext({
    report,
    company: company ?? undefined,
    site: site ?? undefined,
    visitDate,
    visitPeriod: computedVisitPeriod,
    frequencyMonths,
    nostroRecapito,
  });

  const view = createReportView({ template, report, context });
  const comments = report.data.comments ?? {};
  const sectionsWithComments = await Promise.all(
    view.sections.map(async (section) => {
      const comment = comments[section.id];
      const mediaItems = comment?.media ?? [];
      const mappedMedia = await Promise.all(
        mediaItems.map(async (media) => {
          const cached =
            !media.dataUrl && media.storagePath
              ? await getCachedReportAttachment(media.storagePath)
              : null;
          return {
            id: media.id,
            name: media.name,
            dataUrl: media.dataUrl ?? cached ?? undefined,
            storagePath: media.storagePath,
            mimeType: media.mimeType,
            byteSize: media.byteSize,
            createdAt: media.createdAt,
          };
        })
      );

      return {
        ...section,
        comment: comment?.text,
        commentMedia: mappedMedia.length > 0 ? mappedMedia : undefined,
      };
    })
  );
  const viewWithComments = {
    ...view,
    sections: sectionsWithComments,
  };

  // Create descriptor
  const descriptor: ReportRenderDescriptor = {
    preparedAt: new Date().toISOString(),
    dataSources: {
      reportData: report.status === 'finalized' ? 'snapshot' : 'live',
      companyData: report.status === 'finalized' ? 'snapshot' : 'live',
      siteData: report.status === 'finalized' ? 'snapshot' : 'live',
      settingsData: 'live',
    },
    sections: Object.fromEntries(viewWithComments.sections.map((section) => [section.id, {}])),
  };

  return {
    report,
    descriptor,
    view: viewWithComments,
  };
}

interface DraftContextOptions {
  report: Report;
  company?: Company;
  site?: Site | null;
  visitDate: string;
  visitPeriod?: string;
  frequencyMonths?: number;
  nostroRecapito: string;
}

function buildScorecardFromReportValues(
  values: Record<string, unknown>,
  fallbackDate: string
): Array<Record<string, unknown>> {
  const entries = new Map<number, Record<string, unknown>>();

  for (const [key, value] of Object.entries(values)) {
    let parsed: { sectionId: string; fieldId: string; indices: number[] };
    try {
      parsed = parseKeypath(key);
    } catch {
      continue;
    }

    if (parsed.sectionId !== SCORECARD_SECTION_ID) continue;
    const index = parsed.indices[0] ?? 0;
    const entry = entries.get(index) ?? {
      id: `scorecard-${index + 1}`,
      date: fallbackDate,
    };

    if (parsed.fieldId === SCORECARD_DATE_FIELD_ID) {
      entry.date = typeof value === 'string' ? value : fallbackDate;
    } else {
      const mappedKey = SCORECARD_FIELD_MAP[parsed.fieldId];
      if (mappedKey) {
        entry[mappedKey] = value;
      }
    }

    entries.set(index, entry);
  }

  return Array.from(entries.entries())
    .sort(([a], [b]) => a - b)
    .map(([index, entry]) => ({
      ...entry,
      label: (entry.label as string | undefined) ?? `Valutazione ${index + 1}`,
    }));
}

export function buildDraftContext({
  report,
  company,
  site,
  visitDate,
  visitPeriod,
  frequencyMonths,
  nostroRecapito,
}: DraftContextOptions): DataContext {
  const visitInput: Partial<VisitContextType> = {
    date: visitDate,
    inspector: report.meta.inspector,
  };

  if (visitPeriod) {
    visitInput.period = visitPeriod;
  }

  if (typeof frequencyMonths === 'number') {
    visitInput.frequencyMonths = frequencyMonths;
  }

  const attendeeLines = splitAttendeeLines(
    report.data.values[VISIT_ATTENDEES_KEYPATH] as string | undefined
  );
  if (attendeeLines.length > 0) {
    visitInput.attendees = attendeeLines
      .map((line) => parseAttendeeLine(line))
      .filter((entry) => entry.name.length > 0);
  }

  const visit = VisitContext.parse(visitInput);
  const scorecardEntries = buildScorecardFromReportValues(report.data.values, visitDate);
  if (scorecardEntries.length > 0) {
    (visit as Record<string, unknown>).scorecard = {
      byDate: scorecardEntries,
    };
  }

  const context: DataContext = {
    visit,
    variables: {
      visitDate,
      visitPeriod,
      frequencyMonths,
      nostroRecapito,
    },
  };

  if (company) {
    context.company = company;
  }

  if (site ?? null) {
    context.site = site ?? undefined;
  }

  return context;
}
